import { useQuery } from "@tanstack/react-query";
import { IndianRupee, TrendingUp, Truck, CreditCard, RotateCcw, Wallet } from "lucide-react";
import api, { fmtINR, BACKEND } from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

export default function Accounting() {
  const { data: s } = useQuery({ queryKey: ["accounting"], queryFn: async () => (await api.get("/admin/accounting/summary")).data });
  const { data: ledger } = useQuery({ queryKey: ["admin-ledger"], queryFn: async () => (await api.get("/accounting/ledger?limit=50")).data });

  if (!s) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div data-testid="accounting-page">
      <PageHeader title="Accounting" sub="Sales, costs, fees and profit — estimated vs actual." testid="accounting-title">
        <Button data-testid="export-orders-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800"
          onClick={() => window.open(`${BACKEND}/api/export/orders`, "_blank")}><Download size={13} /> Orders CSV</Button>
        <Button data-testid="export-products-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800"
          onClick={() => window.open(`${BACKEND}/api/export/products`, "_blank")}><Download size={13} /> Products CSV</Button>
        <Button data-testid="export-ledger-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800"
          onClick={() => window.open(`${BACKEND}/api/export/ledger`, "_blank")}><Download size={13} /> Ledger CSV</Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        <StatCard testid="acc-sales" icon={IndianRupee} label="Total Sales" value={fmtINR(s.sales)} />
        <StatCard testid="acc-product-revenue" icon={IndianRupee} label="Product Revenue" value={fmtINR(s.product_revenue)} accent="text-blue-400" />
        <StatCard testid="acc-shipping" icon={Truck} label="Shipping Collected" value={fmtINR(s.shipping_collected)} accent="text-cyan-400" />
        <StatCard testid="acc-fees" icon={CreditCard} label="Fees (Payment+COD)" value={fmtINR(s.payment_fees + s.cod_fees)} accent="text-amber-400" />
        <StatCard testid="acc-refunds" icon={RotateCcw} label="Refunds" value={fmtINR(s.refunds)} accent="text-rose-400" />
        <StatCard testid="acc-est-profit" icon={TrendingUp} label="Estimated Profit" value={fmtINR(s.estimated_profit)} accent="text-emerald-400" />
        <StatCard testid="acc-actual-profit" icon={TrendingUp} label="Actual Profit" value={fmtINR(s.actual_profit)} sub="Delivered orders" accent="text-emerald-400" />
        <StatCard testid="acc-outstanding" icon={Wallet} label="Agents with Dues" value={(s.agent_balances || []).filter((a) => a.balance < 0).length} accent="text-violet-400" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="agent-balances">
          <h3 className="mb-3 text-sm font-semibold text-slate-200">Agent Balances (credit − debit)</h3>
          {!s.agent_balances?.length ? <EmptyState title="No agents" /> : (
            <div className="space-y-2">
              {s.agent_balances.map((a) => (
                <div key={a.agent_id} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-slate-200">{a.business_name}</p>
                    <p className="text-[10px] text-slate-500">{a.name}</p>
                  </div>
                  <p className={`text-sm font-bold ${a.balance >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtINR(a.balance)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="ledger-table">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">Agent Ledger (latest)</h3>
            {ledger && (
              <p className="text-[10px] text-slate-500">
                Credits <span className="text-emerald-400">{fmtINR(ledger.credits)}</span> · Debits <span className="text-rose-400">{fmtINR(ledger.debits)}</span>
              </p>
            )}
          </div>
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {(ledger?.items || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
                <div>
                  <p className="text-xs text-slate-200">{e.note}</p>
                  <p className="font-mono text-[10px] text-slate-500">{e.agent_name} · {e.ref}</p>
                </div>
                <p className={`font-mono text-xs font-semibold ${e.type === "credit" ? "text-emerald-400" : "text-rose-400"}`}>
                  {e.type === "credit" ? "+" : "−"}{fmtINR(e.amount)}
                </p>
              </div>
            ))}
            {!ledger?.items?.length && <p className="py-8 text-center text-xs text-slate-500">No ledger entries yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
